import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Input,
  Select,
  Modal,
  Row,
  Col,
  Descriptions,
  Divider,
  Alert,
} from 'antd';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  CalendarOutlined,
  FileTextOutlined,
  GoogleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { jobPostingQueryKeys } from '../../../lib/queryClient';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { formatGoogleCalendarDate } from '../../../utils/interviewDisplay';

dayjs.locale('tr');

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

interface InterviewSummary {
  id: number;
  interviewDate: string;
  interviewType: string;
  status: string;
  notes?: string;
  interviewerName?: string;
  location?: string;
}

interface JobApplication {
  id: number;
  jobPostingId: number;
  jobTitle: string;
  companyName: string;
  coverLetter: string;
  resumeFileName?: string;
  resumeFileSize?: number;
  status: string;
  appliedAt: string;
  reviewedAt?: string;
  adminNotes?: string;
  latestInterview?: InterviewSummary | null;
}

interface Interview extends InterviewSummary {
  jobApplicationId?: number;
  interviewFeedback?: string;
  rating?: number;
}

const getInterviewTypeText = (type: string) => {
  switch (type) {
    case 'Phone':
      return 'Telefon';
    case 'Video':
    case 'Online':
      return 'Online';
    case 'In-Person':
      return 'Yüz Yüze';
    default:
      return type;
  }
};

const getInterviewStatusText = (status: string) => {
  switch (status) {
    case 'Scheduled':
      return 'Planlandı';
    case 'Completed':
      return 'Tamamlandı';
    case 'Cancelled':
      return 'İptal Edildi';
    case 'Rescheduled':
      return 'Yeniden Planlandı';
    default:
      return status;
  }
};

const getInterviewStatusColor = (status: string) => {
  switch (status) {
    case 'Scheduled':
      return 'blue';
    case 'Completed':
      return 'green';
    case 'Cancelled':
      return 'red';
    case 'Rescheduled':
      return 'orange';
    default:
      return 'default';
  }
};

const JobApplicationsTab: React.FC = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const openedFromUrlRef = useRef(false);

  const { data: applications = [], isLoading: loading } = useQuery({
    queryKey: jobPostingQueryKeys.myApplications,
    queryFn: async () => {
      const res = await apiService.getMyJobApplications(1, 100);
      if (!res.success) throw new Error(res.message || 'Başvurular yüklenirken hata oluştu');
      return res.data?.data ?? [];
    },
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'orange';
      case 'reviewed':
        return 'blue';
      case 'accepted':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    if (status.toLowerCase() !== 'pending') {
      return 'İncelendi';
    }
    return 'Beklemede';
  };

  const loadInterviews = useCallback(async (application: JobApplication) => {
    try {
      const interviewResponse = await apiService.getInterviews(application.id);
      if (interviewResponse.success && interviewResponse.data) {
        const interviewData = Array.isArray(interviewResponse.data)
          ? interviewResponse.data
          : [interviewResponse.data];
        setInterviews(interviewData);
      } else if (application.latestInterview) {
        setInterviews([{ ...application.latestInterview, jobApplicationId: application.id }]);
      } else {
        setInterviews([]);
      }
    } catch {
      if (application.latestInterview) {
        setInterviews([{ ...application.latestInterview, jobApplicationId: application.id }]);
      } else {
        setInterviews([]);
      }
    }
  }, []);

  const handleViewDetails = async (application: JobApplication) => {
    setSelectedApplication(application);
    setDetailModalVisible(true);
    await loadInterviews(application);
  };

  useEffect(() => {
    if (openedFromUrlRef.current || applications.length === 0) return;

    const applicationIdParam = searchParams.get('applicationId');
    if (!applicationIdParam) return;

    const applicationId = Number(applicationIdParam);
    if (Number.isNaN(applicationId)) return;

    const application = applications.find((app: JobApplication) => app.id === applicationId);
    if (!application) return;

    openedFromUrlRef.current = true;
    void handleViewDetails(application);

    const next = new URLSearchParams(searchParams);
    next.delete('applicationId');
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [applications, searchParams, setSearchParams]);

  const handleDownloadResume = (application: JobApplication) => {
    if (!application.resumeFileName) {
      toast.warning('CV dosyası bulunamadı');
      return;
    }
    toast.info('CV indirme özelliği yakında eklenecek');
  };

  const addToGoogleCalendar = (interview: Interview, application: JobApplication) => {
    const startDate = dayjs(interview.interviewDate);
    const endDate = startDate.add(1, 'hour');
    const title = encodeURIComponent(`${application.jobTitle} - Mülakat`);
    const details = encodeURIComponent(
      `Şirket: ${application.companyName}\n` +
        `Mülakatçı: ${interview.interviewerName || 'Bilinmiyor'}\n` +
        `Tür: ${getInterviewTypeText(interview.interviewType)}\n` +
        (interview.location ? `Konum: ${interview.location}\n` : '') +
        (interview.notes ? `Notlar: ${interview.notes}` : '')
    );
    const location = encodeURIComponent(interview.location || '');
    const start = formatGoogleCalendarDate(startDate);
    const end = formatGoogleCalendarDate(endDate);
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
    window.open(googleCalendarUrl, '_blank');
  };

  const addToICalendar = (interview: Interview, application: JobApplication) => {
    const startDate = dayjs(interview.interviewDate);
    const endDate = startDate.add(1, 'hour');
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Menteem//EN',
      'BEGIN:VEVENT',
      `DTSTART:${formatGoogleCalendarDate(startDate)}`,
      `DTEND:${formatGoogleCalendarDate(endDate)}`,
      `SUMMARY:${application.jobTitle} - Mülakat`,
      `DESCRIPTION:Şirket: ${application.companyName}\\nMülakatçı: ${interview.interviewerName || 'Bilinmiyor'}\\nTür: ${getInterviewTypeText(interview.interviewType)}${interview.notes ? `\\nNotlar: ${interview.notes}` : ''}`,
      interview.location ? `LOCATION:${interview.location}` : '',
      'END:VEVENT',
      'END:VCALENDAR',
    ]
      .filter(Boolean)
      .join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `mulakat-${application.jobTitle}-${startDate.format('YYYY-MM-DD')}.ics`;
    link.click();
  };

  const getInterviewForApplication = (applicationId: number) => {
    const fromState = interviews.find((i) => i.jobApplicationId === applicationId);
    if (fromState) return fromState;
    const app = applications.find((a: JobApplication) => a.id === applicationId);
    return app?.latestInterview
      ? { ...app.latestInterview, jobApplicationId: applicationId }
      : undefined;
  };

  const upcomingInterviews = applications
    .filter((app: JobApplication) => {
      const interview = app.latestInterview;
      return (
        interview &&
        interview.status === 'Scheduled' &&
        dayjs(interview.interviewDate).isAfter(dayjs().subtract(1, 'hour'))
      );
    })
    .sort(
      (a: JobApplication, b: JobApplication) =>
        dayjs(a.latestInterview!.interviewDate).valueOf() -
        dayjs(b.latestInterview!.interviewDate).valueOf()
    );

  const filteredApplications = applications.filter((app: JobApplication) => {
    const matchesSearch =
      app.jobTitle.toLowerCase().includes(searchText.toLowerCase()) ||
      app.companyName.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = !statusFilter || app.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'İş İlanı',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      render: (text: string, record: JobApplication) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.companyName}</div>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>,
    },
    {
      title: 'Mülakat',
      key: 'interview',
      render: (_: unknown, record: JobApplication) => {
        const interview = record.latestInterview;
        if (!interview) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {dayjs(interview.interviewDate).format('DD.MM.YYYY HH:mm')}
            </div>
            <Space size={4} wrap style={{ marginTop: 4 }}>
              <Tag color={getInterviewStatusColor(interview.status)}>
                {getInterviewStatusText(interview.status)}
              </Tag>
              <Tag>{getInterviewTypeText(interview.interviewType)}</Tag>
            </Space>
          </div>
        );
      },
    },
    {
      title: 'Başvuru Tarihi',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: JobApplication) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
            Detay
          </Button>
          {record.resumeFileName && (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadResume(record)}
            >
              CV İndir
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <PanelTabCard
        title="İş Başvurularım"
        description="Yaptığınız iş başvurularını ve planlanan mülakatları görüntüleyebilirsiniz."
      >
        <TabListLoading loading={loading}>
          {upcomingInterviews.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Yaklaşan mülakatlarınız"
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {upcomingInterviews.map((app: JobApplication) => (
                    <div key={app.id}>
                      <Text strong>{app.jobTitle}</Text>
                      <Text> — {app.companyName} · </Text>
                      <Text>
                        {dayjs(app.latestInterview!.interviewDate).format('DD MMMM YYYY, HH:mm')}
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleViewDetails(app)}
                        style={{ paddingLeft: 8 }}
                      >
                        Detayları gör
                      </Button>
                    </div>
                  ))}
                </div>
              }
            />
          )}

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="İş ilanı veya şirket ara..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Durum filtresi"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="pending">Beklemede</Option>
                <Option value="reviewed">İncelendi</Option>
              </Select>
            </Col>
          </Row>

          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={filteredApplications}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} başvuru`,
            }}
          />
        </TabListLoading>
      </PanelTabCard>

      <Modal
        title="Başvuru Detayları"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>,
        ]}
        width={800}
      >
        {selectedApplication && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="İş İlanı">
                <Text strong>{selectedApplication.jobTitle}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Şirket">
                <Text>{selectedApplication.companyName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(selectedApplication.status)}>
                  {getStatusText(selectedApplication.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Başvuru Tarihi">
                {dayjs(selectedApplication.appliedAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
              {selectedApplication.reviewedAt && (
                <Descriptions.Item label="İnceleme Tarihi">
                  {dayjs(selectedApplication.reviewedAt).format('DD.MM.YYYY HH:mm')}
                </Descriptions.Item>
              )}
              {selectedApplication.resumeFileName && (
                <Descriptions.Item label="CV Dosyası">
                  <Space>
                    <FileTextOutlined />
                    <Text>{selectedApplication.resumeFileName}</Text>
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadResume(selectedApplication)}
                    >
                      İndir
                    </Button>
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <div>
              <Title level={5}>Ön Yazı</Title>
              <div
                style={{
                  background: 'var(--bg-tertiary)',
                  padding: 16,
                  borderRadius: 6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedApplication.coverLetter}
              </div>
            </div>

            {selectedApplication.adminNotes && (
              <>
                <Divider />
                <div>
                  <Title level={5}>Şirket Notları</Title>
                  <div
                    style={{
                      background: '#f0f8ff',
                      padding: 16,
                      borderRadius: 6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {selectedApplication.adminNotes}
                  </div>
                </div>
              </>
            )}

            {(() => {
              const interview = getInterviewForApplication(selectedApplication.id);
              if (!interview) return null;

              return (
                <>
                  <Divider />
                  <div>
                    <Title level={5}>Mülakat Bilgileri</Title>
                    <div
                      style={{
                        background: '#f0fdf4',
                        padding: 16,
                        borderRadius: 6,
                        marginBottom: 16,
                      }}
                    >
                      <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Mülakat Tarihi">
                          {dayjs(interview.interviewDate).format('DD MMMM YYYY, dddd, HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mülakat Türü">
                          {getInterviewTypeText(interview.interviewType)}
                        </Descriptions.Item>
                        {interview.interviewerName && (
                          <Descriptions.Item label="Mülakatçı">
                            {interview.interviewerName}
                          </Descriptions.Item>
                        )}
                        {interview.location && (
                          <Descriptions.Item label="Konum / Meeting Link">
                            <a href={interview.location} target="_blank" rel="noopener noreferrer">
                              {interview.location}
                            </a>
                          </Descriptions.Item>
                        )}
                        {interview.notes && (
                          <Descriptions.Item label="Notlar">
                            <div style={{ whiteSpace: 'pre-wrap' }}>{interview.notes}</div>
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Durum">
                          <Tag color={getInterviewStatusColor(interview.status)}>
                            {getInterviewStatusText(interview.status)}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <Title level={5}>Takvime Ekle</Title>
                      <Space>
                        <Button
                          type="default"
                          icon={<GoogleOutlined />}
                          onClick={() => addToGoogleCalendar(interview, selectedApplication)}
                        >
                          Google Takvim'e Ekle
                        </Button>
                        <Button
                          type="default"
                          icon={<CalendarOutlined />}
                          onClick={() => addToICalendar(interview, selectedApplication)}
                        >
                          iCalendar İndir
                        </Button>
                      </Space>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>
    </>
  );
};

export default JobApplicationsTab;
